import React, { useCallback, useMemo, useState } from 'react';
import { toast as message } from 'sonner';
import { useSidePanel } from '../SidePanelContext';
import { useFieldDependentTable } from '../../../utils/metadata/tables';
import { useGlobalMetadata } from '../../../utils/providers';
import { useMyCapabilities } from '../../../utils/metadata/capabilities';
import { handleOperationError, updateRecord } from '../../../utils/supabase-operations';
import { invalidateAndRefreshAllQueriesForTable } from '../../../utils/metadata';
import { supabaseClient } from '../../../utils/supabase';
import RecordLookupSelect from '../../RecordLookupSelect';
import { useMerchantReps, useInvalidateMerchantReps, useMerchantRepHistory, type MerchantRep } from '../../../utils/useMerchantReps';
import type { RelationshipLookup } from '../../../types/metadata';
import type { BaseRecord } from '../../../types/api';
import MerchantSubmissionsTab from './MerchantSubmissionsTab';
import MerchantDocsTab from './MerchantDocsTab';
import MerchantBasicInfoTab from './MerchantBasicInfoTab';
import MerchantCallNotesTab from './MerchantCallNotesTab';
import MerchantLeadsTab from './MerchantLeadsTab';
import MerchantDealPrepTab from './MerchantDealPrepTab';
import GmailRecordEmailTab from '../../GmailRecordEmailTab';
import RecordHistory from '../../RecordHistory';
import { MerchantPrimaryOverrideControl } from '../../PrimaryApprovalActions';
import type { PanelRecord } from '../SidePanelContext';
import { merchantDetailRequest } from '../../../utils/destinationQueries/merchantPanel';
import { AppOutDialog } from '../../AppOut/AppOutDialog';
import { useAppoutSender } from '../../AppOut/appout';
import './MerchantPanel.css';

const TABS = ['Submissions', 'Deal Prep', 'Basic Info', 'Call Notes', 'Email', 'Leads', 'History'] as const;
type TabName = typeof TABS[number];

interface MerchantPanelBodyProps {
  record: PanelRecord;
}

function MerchantPanelBody({ record }: MerchantPanelBodyProps) {
  const { innerTab, setInnerTab } = useSidePanel();
  const { data: metadata } = useGlobalMetadata();
  const { hasCapability } = useMyCapabilities();
  // Imported leads are for lead import operators only (BIZ Z42).
  const tabs: readonly TabName[] = hasCapability('lead_import') ? TABS : TABS.filter((t) => t !== 'Leads');
  const activeTab = (tabs.includes(innerTab as TabName) ? innerTab : 'Submissions') as TabName;

  const tableInfo = metadata?.tableData?.byName?.['merchant'];
  const schemaName = tableInfo?.schema_name || 'public';

  const merchantRequest = useMemo(
    () => merchantDetailRequest(Number(record.id), schemaName),
    [record.id, schemaName],
  );

  const { data: merchant, isLoading } = useFieldDependentTable({
    enabled: !!record.id,
    ...merchantRequest,
  });

  const m = merchant as Record<string, any> | null;

  return (
    <>
      {/* Identity: business name + reps, always at the top */}
      <MerchantIdentityHeader merchant={m} merchantId={Number(record.id)} />

      <MerchantPrimaryOverrideControl merchantId={Number(record.id)} />

      {/* DriveBar — always visible above tabs */}
      <MerchantDocsTab merchantId={Number(record.id)} />

      {/* Inner tab bar */}
      <div className="sky-inner-tabs" role="tablist" aria-label="Merchant sections">
        {tabs.map(t => (
          <button
            key={t}
            role="tab"
            aria-selected={activeTab === t}
            className={`sky-inner-tab ${activeTab === t ? 'sky-inner-tab--active' : ''}`}
            onClick={() => setInnerTab(t)}
          >
            {t === 'Submissions' ? 'Merchant Submissions' : t}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="sky-panel-body" data-testid="merchant-detail" aria-busy={isLoading}>
        {activeTab === 'Submissions' && (
          <MerchantSubmissionsTab merchant={m} merchantId={Number(record.id)} isLoading={isLoading} />
        )}
        {activeTab === 'Deal Prep' && (
          <MerchantDealPrepTab merchantId={Number(record.id)} />
        )}
        {activeTab === 'Basic Info' && (
          <MerchantBasicInfoTab merchant={m} isLoading={isLoading} />
        )}
        {activeTab === 'Call Notes' && (
          <MerchantCallNotesTab merchantId={Number(record.id)} />
        )}
        {activeTab === 'Email' && (
          <GmailRecordEmailTab tableName="merchant" recordId={Number(record.id)} />
        )}
        {activeTab === 'Leads' && (
          <MerchantLeadsTab merchantId={Number(record.id)} />
        )}
        {activeTab === 'History' && (
          <>
            <MerchantRepHistory merchantId={Number(record.id)} />
            <RecordHistory tableName="merchant" recordId={Number(record.id)} />
          </>
        )}
      </div>
    </>
  );
}

/* ── Identity header: business name + main rep + additional reps ──────────
 * Main rep sits at the top next to the business name (it isn't surfaced
 * anywhere else in the CRM today). "Additional reps" is everyone else who can
 * see the merchant — merchant-level grants and reps rolled up from the
 * merchant's submissions (core_crm.get_merchant_reps). The list stays compact:
 * a couple of names, then "+N", with the full roster on hover. */

function relName(rel: any): string {
  if (!rel) return '';
  if (typeof rel === 'string') return rel;
  return rel.display_name || rel.name || rel.label || rel.full_name || '';
}

/**
 * Synthetic relationship metadata for the rep search select — rep_link.rep is
 * not registered as a generic form field, and RecordLookupSelect only reads
 * target_table_name/target_label_field. Mirrors the submission panel's card.
 */
const REP_RELATIONSHIP: RelationshipLookup = {
  id: 0,
  col_name: 'rep',
  source_table_name: 'rep_link',
  source_schema_name: 'public',
  target_table_name: 'rep',
  target_schema_name: 'public',
  target_label_field: 'name',
  cascade_delete: false,
  expand_fields: false,
  expand_prefix: null,
  foreign_key_name: '',
  created_at: '',
  updated_at: '',
  created_by: 0,
  updated_by: 0,
};

function MerchantIdentityHeader({ merchant, merchantId }: { merchant: Record<string, any> | null; merchantId: number }) {
  const { data: reps = [] } = useMerchantReps(merchantId);
  const invalidateReps = useInvalidateMerchantReps();
  const { hasCapability } = useMyCapabilities();
  const canEdit = hasCapability('edit_deals');
  const [managing, setManaging] = useState(false);
  const [editingMainRep, setEditingMainRep] = useState(false);
  const [savingMainRep, setSavingMainRep] = useState(false);
  // REG-918: Hilton App Out senders only; everyone else has no button.
  const appoutSender = useAppoutSender();
  const [appoutOpen, setAppoutOpen] = useState(false);

  const businessName = merchant?.display_name_calc || merchant?.business_name || `Merchant #${merchantId}`;

  // Prefer the row flagged is_main; fall back to merchant.main_rep_rel so the
  // main rep still shows before get_merchant_reps resolves.
  const mainFromReps = reps.find((r) => r.is_main);
  const mainRepName = mainFromReps?.rep_name || relName(merchant?.main_rep_rel);
  const mainRepId = mainFromReps?.rep ?? (
    merchant?.main_rep == null ? null : Number(merchant.main_rep)
  );
  const mainRepRecord = mainRepId == null
    ? null
    : ({ id: mainRepId, name: mainRepName || `Rep #${mainRepId}` } as unknown as BaseRecord);

  const additional = reps.filter((r) => !r.is_main && r.rep !== mainRepId);

  const handleMainRepChange = useCallback(async (selectedRep: BaseRecord | null) => {
    if (!merchant || selectedRep == null) return;
    const nextRepId = Number(selectedRep.id);
    const previousRepId = merchant.main_rep == null ? null : Number(merchant.main_rep);
    if (!Number.isFinite(nextRepId)) return;
    if (nextRepId === previousRepId) {
      setEditingMainRep(false);
      return;
    }

    setSavingMainRep(true);
    try {
      const result = await updateRecord({
        tableName: 'merchant',
        schema: 'public',
        data: { main_rep: nextRepId },
        whereClause: { id: merchantId },
        options: {
          invalidateCache: false,
          fieldName: 'main_rep',
          expectedValues: { main_rep: previousRepId },
          expectedValueTypes: { main_rep: 'bigint' },
        },
      });
      if (result.error) throw result.error;
      await Promise.all([
        invalidateAndRefreshAllQueriesForTable('merchant'),
        invalidateReps(merchantId),
      ]);
      message.success('Main rep updated');
      setEditingMainRep(false);
    } catch (error) {
      const info = handleOperationError(error, 'Failed to update main rep');
      message.error(info.userMessage);
    } finally {
      setSavingMainRep(false);
    }
  }, [merchant, merchantId, invalidateReps]);

  return (
    <div className="sky-mp-identity">
      <AppOutDialog merchantId={appoutOpen ? merchantId : null} label={businessName} onClose={() => setAppoutOpen(false)} />
      <div className="sky-mp-identity-top">
        <div className="sky-mp-identity-name" title={businessName}>{businessName}</div>
        {appoutSender && (
          <button
            type="button"
            className="sky-mp-appout"
            onClick={() => setAppoutOpen(true)}
            title={`Send the Hilton App Out from ${appoutSender.from_email}`}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" /></svg>
            App Out
          </button>
        )}
        {canEdit && (
          <button
            className={`sky-mp-identity-manage${managing ? ' sky-mp-identity-manage--active' : ''}`}
            onClick={() => setManaging((v) => !v)}
            title="Manage additional merchant-wide reps"
          >
            {managing ? (
              'Done'
            ) : (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
                Manage
              </>
            )}
          </button>
        )}
      </div>
      <div className="sky-mp-identity-reps">
        <div className="sky-mp-identity-rep">
          <span className="sky-mp-identity-rep-label">MAIN REP</span>
          {canEdit && editingMainRep ? (
            <div className="sky-mp-main-rep-editor">
              <RecordLookupSelect
                name="main_rep"
                relationship={REP_RELATIONSHIP}
                record={mainRepRecord}
                disabled={savingMainRep}
                schema="public"
                onChange={(rep) => void handleMainRepChange(rep)}
              />
              <button
                type="button"
                className="sky-mp-main-rep-cancel"
                disabled={savingMainRep}
                onClick={() => setEditingMainRep(false)}
                aria-label="Cancel changing main rep"
                title="Cancel"
              >
                ×
              </button>
            </div>
          ) : canEdit ? (
            <button
              type="button"
              className="sky-mp-identity-rep-value sky-mp-main-rep-edit"
              onClick={() => setEditingMainRep(true)}
              title="Change main rep"
            >
              {mainRepName || '—'}
            </button>
          ) : (
            <span className="sky-mp-identity-rep-value">{mainRepName || '—'}</span>
          )}
        </div>
        {additional.length > 0 && (
          <span className="sky-mp-identity-rep sky-mp-identity-rep--extra">
            <AdditionalReps reps={additional} />
          </span>
        )}
      </div>
      {canEdit && managing && (
        <MerchantRepsEditor merchantId={merchantId} reps={reps} />
      )}
    </div>
  );
}

/**
 * Add/remove merchant-wide reps (rep_link with the merchant set and no
 * submission). Only these blanket grants are editable here; reps who appear
 * via a specific submission are managed on that submission's own card, so they
 * are shown read-only and labelled. Gated on edit_deals to match rep_link RLS.
 */
function MerchantRepsEditor({ merchantId, reps }: { merchantId: number; reps: MerchantRep[] }) {
  const invalidateReps = useInvalidateMerchantReps();
  const [selectedRep, setSelectedRep] = useState<BaseRecord | null>(null);
  const [saving, setSaving] = useState(false);
  const [removingRep, setRemovingRep] = useState<number | null>(null);

  // Merchant-wide grants: reachable at the merchant level but not the main rep.
  const merchantWide = reps.filter((r) => r.via_merchant && !r.is_main);
  // Reps present only through a submission — read-only here.
  const viaSubOnly = reps.filter((r) => r.via_submission && !r.via_merchant && !r.is_main);

  const refresh = useCallback(async () => {
    await invalidateReps(merchantId);
    await invalidateAndRefreshAllQueriesForTable('rep_link');
  }, [invalidateReps, merchantId]);

  const handleAdd = useCallback(async () => {
    if (!selectedRep) return;
    setSaving(true);
    try {
      const { error } = await supabaseClient
        .from('rep_link')
        .insert({ merchant: merchantId, rep: Number(selectedRep.id) });
      if (error) throw error;
      await refresh();
      message.success('Rep added');
      setSelectedRep(null);
    } catch (error) {
      const info = handleOperationError(error, 'Failed to add rep');
      message.error(info.userMessage);
    } finally {
      setSaving(false);
    }
  }, [selectedRep, merchantId, refresh]);

  const handleRemove = useCallback(async (repId: number) => {
    setRemovingRep(repId);
    try {
      const { error } = await supabaseClient
        .from('rep_link')
        .delete()
        .eq('merchant', merchantId)
        .eq('rep', repId)
        .is('submission', null);
      if (error) throw error;
      await refresh();
      message.success('Removed');
    } catch (error) {
      const info = handleOperationError(error, 'Failed to remove rep');
      message.error(info.userMessage);
    } finally {
      setRemovingRep(null);
    }
  }, [merchantId, refresh]);

  return (
    <div className="sky-mp-reps-editor">
      {merchantWide.length === 0 && (
        <div className="sky-mp-reps-empty">No merchant-wide reps.</div>
      )}
      {merchantWide.map((r) => (
        <div key={r.rep} className="sky-mp-reps-editor-row">
          <span className="sky-mp-reps-editor-name">{r.rep_name}</span>
          <button
            className="sky-icon-btn"
            title="Remove"
            disabled={removingRep === r.rep}
            onClick={() => void handleRemove(r.rep)}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
        </div>
      ))}
      {viaSubOnly.map((r) => (
        <div key={`sub-${r.rep}`} className="sky-mp-reps-editor-row sky-mp-reps-editor-row--readonly">
          <span className="sky-mp-reps-editor-name">{r.rep_name}</span>
          <span className="sky-mp-reps-editor-via">via submission</span>
        </div>
      ))}
      <div className="sky-mp-reps-editor-add">
        <RecordLookupSelect
          name="rep"
          relationship={REP_RELATIONSHIP}
          record={selectedRep}
          schema="public"
          onChange={setSelectedRep}
        />
        <button
          className="sky-mp-new-form-save"
          disabled={!selectedRep || saving}
          onClick={() => void handleAdd()}
        >
          {saving ? 'Adding…' : 'Add'}
        </button>
      </div>
    </div>
  );
}

/** Access-change log (REG-497 AC#3): who was added to / removed from the
 * merchant's reps and when. Sits above the generic merchant field history. */
function MerchantRepHistory({ merchantId }: { merchantId: number }) {
  const { data: events = [], isLoading } = useMerchantRepHistory(merchantId);
  if (isLoading || events.length === 0) return null;

  const verb = (op: string) =>
    op === 'INSERT' ? 'added' : op === 'DELETE' ? 'removed' : 'updated';

  return (
    <div className="sky-mp-rep-history">
      <div className="sky-mp-rep-history-label">REP ACCESS CHANGES</div>
      {events.slice(0, 20).map((e, i) => (
        <div key={`${e.rep_link_id}-${e.transaction_time}-${i}`} className="sky-mp-rep-history-row">
          <span className={`sky-mp-rep-history-op sky-mp-rep-history-op--${e.operation.toLowerCase()}`}>
            {verb(e.operation)}
          </span>
          <span className="sky-mp-rep-history-name">{e.rep_name || `Rep #${e.rep ?? '?'}`}</span>
          <span className="sky-mp-rep-history-meta">
            {e.changed_by_name || 'system'} · {new Date(e.transaction_time).toLocaleDateString()}
          </span>
        </div>
      ))}
    </div>
  );
}

/** Other-rep disclosure. The roster stays out of the identity header until
 * the user opens the dropdown. */
function AdditionalReps({ reps }: { reps: MerchantRep[] }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="sky-mp-also">
      <button
        type="button"
        className="sky-mp-also-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title="Show other reps"
      >
        <span className="sky-mp-identity-rep-label sky-mp-others-label">OTHER REPS</span>
        <svg className={`sky-mp-also-chevron${open ? ' sky-mp-also-chevron--open' : ''}`} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
      </button>
      {open && (
        <>
          <div className="sky-mp-also-backdrop" onClick={() => setOpen(false)} />
          <div className="sky-mp-also-menu">
            {reps.map((r) => (
              <div key={r.rep} className="sky-mp-also-item">
                <span className="sky-mp-also-item-name">{r.rep_name}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </span>
  );
}

export default React.memo(MerchantPanelBody);
